/* eslint-disable complexity */
import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { ExpanderStack } from "@/components/adg4/price-card/src";
import { Tooltip } from "@/libraries/design/components/tooltip/src/tooltip";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_VALUES } from "@/libraries/web-framework";

import {
    CloudProductKey,
    ENTERPRISE_PRICE_CONTAINER,
    PlanFeatureMessage,
    PricingPeriod
} from "../../price-finder/constants";
import { CtaButton } from "../../price-finder/price-card";
import { BilledAnnuallyButton } from "../../price-finder/price-card/billed-annually-button";
import { AnnualPriceV3 } from "../../price-finder/price-card/price";
import { PlanFeature } from "../../price-finder/price-card/price-card-plan-feature";
import { usePriceFinderContext } from "../../price-finder-v3/context.tsx";
import { useBillingCycle, useSelector } from "../../price-finder-v3/hooks.ts";
import {
    getCurrencyIsoCode,
    getPlanTotalPriceFormatted,
    isUnitCountAboveEnterpriseMinThreshold,
    isUnitCountAboveMaxThreshold
} from "../../price-finder-v3/selectors.ts";
import { RecommendationBanner } from "../jfind-1384-price-card/components/adornments.tsx";

const planInformationCss = css({
    "display": "flex",
    "flexDirection": "column",
    "width": "100%",
    "maxWidth": "363px",
    "flexShrink": 0,
    "paddingBlockStart": token("space.200"),
    "paddingBlockEnd": token("space.600"),
    "paddingInline": token("space.500"),
    "alignItems": "flex-start",
    "alignSelf": "stretch",
    "gap": "16px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& span, & h2": {
        textAlign: "left !important"
    }
});

const planMessagesContainerCss = css({
    marginBlockStart: "-8px",
    paddingBlockEnd: "48px",
    paddingInlineStart: "40px",
    paddingInlineEnd: "40px",
    gap: "16px",
    [media.below.md]: {
        padding: 0
    }
});

const planMessagesCss = css({
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%"
});

const planFeatureRowCss = css({
    display: "flex",
    gap: "16px",
    width: "100%",
    [media.below.md]: {
        flexDirection: "column"
    }
});

const planFeatureColumnCss = css({
    flex: "1 1 50%",
    gap: "16px",
    display: "flex",
    flexDirection: "column",
    minWidth: "0"
});

const baseCardStyles = css({
    width: "100%",
    borderRadius: "20px",
    border: `solid 1px ${wacToken("color.neutral.light300")}`,
    position: "relative"
});

const recommendedCardStyles = css({
    borderRadius: "20px",
    border: `solid 2px ${wacToken("color.primary.blue")}`
});

const cardContentStyles = css({
    display: "flex",
    width: "100%",
    [media.below.md]: {
        flexDirection: "column"
    }
});

const JpyPricingMessage = (
    <div data-testid="enterprise-jpy">
        <FormattedMessage
            id="Pricing.card.body.enterprise.jpy"
            defaultMessage="For pricing details see FAQ below or talk to our sales team"
        />
    </div>
);
const SignupLimitExceededMessage = (
    <div data-testid="enterprise-signup-limit">
        <FormattedMessage
            id="Pricing.card.body.enterprise.overSignupLimit"
            defaultMessage="Talk to our sales team about pricing for Cloud Enterprise"
        />
    </div>
);
export interface EnterpriseCardProps {
    planName: string;
    offeringSku: string;
    isRecommendedPlan: boolean;
    description?: string;
    ctaText: string;
    ctaUrl: string;
    planFeatureMessages?: PlanFeatureMessage[];
    adjacentLowerTierPlanName?: string;
    ctaId: string;
    dataProductKey: CloudProductKey;
    unitCount: number;
    unitTierLowerBound?: string | undefined;
    unitTierUpperBound?: string | undefined;
    numberFormatLocale?: "en-US" | "ja-JP";
}

export const EnterpriseCard = ({
    planName,
    offeringSku,
    isRecommendedPlan,
    description,
    ctaText,
    ctaUrl,
    planFeatureMessages,
    adjacentLowerTierPlanName,
    ctaId,
    dataProductKey,
    unitCount = 0,
    unitTierLowerBound,
    unitTierUpperBound,
    numberFormatLocale = "en-US"
}: EnterpriseCardProps) => {
    const intl = useIntl();
    const formatNumber = new Intl.NumberFormat(numberFormatLocale).format;
    const totalMessages = planFeatureMessages?.length ?? 0;
    const half = Math.ceil(totalMessages / 2);
    const [firstColumn, secColumn] =
        totalMessages > 4
            ? [
                  planFeatureMessages?.slice(0, half),
                  planFeatureMessages?.slice(half)
              ]
            : [planFeatureMessages, []];

    const featureMessagesTitle = adjacentLowerTierPlanName ? (
        <FormattedMessage
            id="Pricing.PriceCard.Enterprise.planFeatureMessageTitle"
            defaultMessage="Everything from {featureMessagePlanName}, plus:"
            values={{
                featureMessagePlanName: adjacentLowerTierPlanName
            }}
        />
    ) : (
        <FormattedMessage
            id="Pricing.PriceCard.featureMessageTitleFree"
            defaultMessage="Includes:"
        />
    );

    // Todo move the price logic to parent
    // Price display logic and helpers
    const [billingFrequency, changeBillingFrequency] = useBillingCycle();
    const { calculationResult } = usePriceFinderContext();
    const currencyISOCode = useSelector((ctx) => getCurrencyIsoCode(ctx));
    const isUnitCountAboveMinThreshold = useSelector((ctx) =>
        isUnitCountAboveEnterpriseMinThreshold(ctx)
    );
    const formattedTotalPrice = useSelector((ctx) =>
        getPlanTotalPriceFormatted(ctx, offeringSku)
    );
    const isOverSignupLimit = useSelector((ctx) =>
        isUnitCountAboveMaxThreshold(ctx, offeringSku)
    );

    // Unit tier message display logic
    const unitTierText = {
        id: "JiraSoftwarePricingPage.priceFinder.cardUnitCountTierRange",
        defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
    };
    const tooltipAriaLabel = intl.formatMessage({
        id: "Pricing.PriceCard.UserTier.MoreInformationAboutTier",
        defaultMessage: "More information about tier"
    });
    const filledSeatsMessage = intl.formatMessage({
        id: "Pricing.PriceCard.UserTier.Details",
        defaultMessage: "Filled seats"
    });
    const availableSeatsMessage = intl.formatMessage({
        id: "Pricing.PriceCard.UserTier.AvailableSeats",
        defaultMessage: "Available seats"
    });
    const formattedUnitTierNode = unitTierLowerBound && unitTierUpperBound && (
        <div css={css({ marginBlockStart: "-16px" })}>
            <FormattedMessage
                {...unitTierText}
                values={{
                    ...DEFAULT_VALUES,
                    lowerLimit: formatNumber(unitTierLowerBound),
                    upperLimit: formatNumber(unitTierUpperBound)
                }}
            />
            <Tooltip
                ariaLabel={tooltipAriaLabel}
                buttonSize="small"
                content={`Tier:
                        ${formatNumber(
                            unitTierLowerBound
                        )} -${" "}${formatNumber(unitTierUpperBound)}

                        ${filledSeatsMessage}:
                        ${formatNumber(unitCount)}

                        ${availableSeatsMessage}:
                        ${formatNumber(unitTierUpperBound - unitCount)}`}
            />
        </div>
    );

    let priceDisplayComponent: ReactNode =
        billingFrequency === "MONTHLY" ? (
            <div data-testid="enterprise-monthly">
                <BilledAnnuallyButton
                    onClick={() =>
                        changeBillingFrequency("ANNUAL" as PricingPeriod)
                    }
                />
                <FormattedMessage
                    id="PricingTeam24.card.body.enterprise.monthly"
                    defaultMessage="Switch to Annual billing above to view Enterprise pricing."
                />
            </div>
        ) : (
            <AnnualPriceV3
                formattedPrice={formattedTotalPrice}
                planName={planName}
                priceCalc={calculationResult}
                priceDisplaySize={
                    currencyISOCode === "JPY" ? "small" : "normal"
                }
                formattedUnitTierNode={formattedUnitTierNode}
            />
        );
    if (billingFrequency === "ANNUAL") {
        if (currencyISOCode === "JPY") {
            priceDisplayComponent = JpyPricingMessage;
        } else if (isOverSignupLimit) {
            priceDisplayComponent = SignupLimitExceededMessage;
        } else if (!isUnitCountAboveMinThreshold) {
            priceDisplayComponent = (
                <div data-testid="enterprise-min-limit">
                    {/* todo: hardcoded message, get it from the parent/context */}
                    <FormattedMessage
                        id={"EnterpriseCard.underMinLimit"}
                        defaultMessage={
                            "Enterprise plan pricing is available when you enter 801 or more users above"
                        }
                    />
                </div>
            );
        }
    }

    return (
        <div css={[baseCardStyles, isRecommendedPlan && recommendedCardStyles]}>
            <div
                css={css({
                    height: "32px",
                    overflow: "hidden",
                    [media.below.md]: {
                        borderStartStartRadius: "18px",
                        borderStartEndRadius: "18px"
                    }
                })}
            >
                {isRecommendedPlan && <RecommendationBanner />}
            </div>
            <div css={cardContentStyles}>
                <div css={planInformationCss}>
                    <h3
                        css={css({
                            fontSize: "32px",
                            lineHeight: "38px",
                            margin: token("space.0")
                        })}
                    >
                        {planName}
                    </h3>
                    {description && <div>{description}</div>}
                    {priceDisplayComponent}
                    <CtaButton
                        id={ctaId}
                        dataProductKey={dataProductKey}
                        edition="enterprise"
                        url={ctaUrl}
                        type={isRecommendedPlan ? "primary" : "secondary"}
                        text={ctaText}
                        isDisabled={false}
                        containerId={ENTERPRISE_PRICE_CONTAINER}
                        aria-labelledby={"enterpriseCta"}
                        target="_self"
                    />
                </div>
                {totalMessages && (
                    <div css={planMessagesContainerCss}>
                        <ExpanderStack heading={featureMessagesTitle}>
                            <div css={planMessagesCss}>
                                <div css={planFeatureRowCss}>
                                    <div css={planFeatureColumnCss}>
                                        {firstColumn?.map(
                                            (planFeatureMessage, idx) => (
                                                <PlanFeature
                                                    key={idx}
                                                    {...planFeatureMessage}
                                                    padded={false}
                                                />
                                            )
                                        )}
                                    </div>
                                    {secColumn?.length > 0 && (
                                        <div css={planFeatureColumnCss}>
                                            {secColumn.map(
                                                (planFeatureMessage, idx) => (
                                                    <PlanFeature
                                                        key={idx}
                                                        {...planFeatureMessage}
                                                        padded={false}
                                                    />
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </ExpanderStack>
                    </div>
                )}
            </div>
        </div>
    );
};
